import { useCallback, useEffect, useState } from 'react';
import { useConfig } from '../../../../context';
import { Log } from '../../../../sentry';
import type { UseStaticBaseProps, UseStaticProps, UseStaticResult } from './types';
import { getErrorMessage, STATIC_EXCEPTION_CATEGORY, StaticException } from '@shared/error';
import { getUrlArray, loadData } from './utils';
import { getLocale } from '../../../../utils';
import { localizeJson } from '../localization';

/**
 * Загрузка статики с CDN / content repo: `fetch` с fallback, отмена при размонтировании, логирование ошибок в Sentry.
 * Режим тела ответа задаётся `responseType`; для JSON можно передать дженерик `useStatic<MyType>({ responseType: 'json', ... })`.
 */
export function useStatic<T = unknown>(props: UseStaticBaseProps & { responseType: 'json' }): UseStaticResult<T>;
export function useStatic(props: UseStaticBaseProps & { responseType: 'text' }): UseStaticResult<string>;
export function useStatic(props: UseStaticBaseProps & { responseType: 'blob' }): UseStaticResult<Blob>;
export function useStatic(props: UseStaticBaseProps & { responseType: 'arrayBuffer' }): UseStaticResult<ArrayBuffer>;
export function useStatic(props: UseStaticProps): UseStaticResult<unknown> {
	const { path, fallback, contentRepo = true, responseType, skip = false } = props;

	const {
		global: {
			companyInfo: { iataCode }
		}
	} = useConfig();

	const [data, setData] = useState<unknown>(undefined);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | undefined>(undefined);

	const logStaticError = useCallback(
		(message: string, extraData?: Record<string, string | undefined>) => {
			Log({
				category: STATIC_EXCEPTION_CATEGORY,
				level: 'error',
				message,
				data: {
					path,
					fallback,
					...extraData
				}
			});
		},
		[path, fallback]
	);

	const handleSetError = (error: unknown) => {
		setError(error instanceof Error ? error : new Error(getErrorMessage(error)));
	};

	useEffect(() => {
		if (!path || skip) {
			setData(undefined);
			setError(undefined);
			setLoading(false);

			return undefined;
		}

		const controller = new AbortController();
		const { signal } = controller;

		const run = async (): Promise<void> => {
			setData(undefined);
			setError(undefined);
			setLoading(true);

			let urls: string[];

			try {
				urls = getUrlArray(path, fallback, contentRepo, iataCode);
			} catch (error) {
				if (signal.aborted) {
					setLoading(false);

					return;
				}

				handleSetError(error);

				logStaticError('useStatic: неверная конфигурация URL', {
					errorMessage: getErrorMessage(error)
				});

				setLoading(false);

				return;
			}

			for (const url of urls) {
				try {
					const data = await loadData(url, signal, responseType);

					if (signal.aborted) {
						setLoading(false);

						return;
					}

					const localizedData = responseType === 'json' ? localizeJson(data, getLocale()) : data;

					setData(localizedData);
					setLoading(false);

					return;
				} catch (error) {
					if (signal.aborted) {
						setLoading(false);

						return;
					}

					logStaticError(`useStatic: ошибка загрузки по адресу ${url}`, {
						resultUrl: url,
						errorMessage: getErrorMessage(error)
					});
				}
			}

			if (!signal.aborted) {
				const error = new StaticException('useStatic: не удалось загрузить ресурс');
				handleSetError(error);
				logStaticError(error.message);

				setLoading(false);
			}
		};

		run();

		return () => {
			controller.abort();
		};
	}, [path, fallback, contentRepo, responseType, iataCode, logStaticError, skip]);

	return {
		data,
		loading,
		error
	};
}
